/* eslint-disable max-lines */
//
//  TokenUtilPreviewReferenceInfoGenerator.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { BlurType, BorderPosition, ShadowType, TokenTransform, TokenUtilPreviewSegmentGenerator, } from "../../exports";
import { BorderStyle } from "../../model/enums/SDKBorderStyle";
import { TextDecoration } from "../../model/enums/SDKTextDecoration";
import { TokenType } from "../../model/enums/SDKTokenType";
import { VisibilityType } from "../../model/enums/SDKVisibilityType";
import { ColorToken } from "../../model/tokens/SDKColorToken";
import { DimensionToken, } from "../../model/tokens/SDKDimensionToken";
import { sureOf } from "../../utils/CommonUtils";
import { TokenUtils } from "../../utils/TokenUtils";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Utility
export class TokenUtilPreviewReferenceInfoGenerator {
    resolvedInfoForSingleLayerToken(token, allTokens) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10;
        switch (token.tokenType) {
            case TokenType.color: {
                const t = token;
                const reference = TokenUtils.lookupReference((_a = t.value) === null || _a === void 0 ? void 0 : _a.referencedTokenId, allTokens);
                return {
                    alwaysShown: [
                        {
                            // Color
                            key: [this.label("Color")],
                            value: [
                                {
                                    value: ColorToken.colorValueToHex6(t.value).toUpperCase(),
                                    prefersReferenceName: true,
                                    referencedName: (_b = reference === null || reference === void 0 ? void 0 : reference.name) !== null && _b !== void 0 ? _b : undefined,
                                    referenceResolutionFailed: TokenUtils.isReferenceResolutionFailed(token),
                                    referenceOriginRemote: !!((_c = token.origin) === null || _c === void 0 ? void 0 : _c.referenceOriginRemote),
                                    referenceOriginName: (_e = (_d = token.origin) === null || _d === void 0 ? void 0 : _d.referenceOriginName) !== null && _e !== void 0 ? _e : undefined,
                                },
                            ],
                        },
                        {
                            // Opacity
                            key: [this.label("Opacity")],
                            value: [
                                {
                                    value: `${Math.round(t.value.opacity.measure * 100)}%`,
                                    prefersReferenceName: false,
                                    referencedName: (_f = reference === null || reference === void 0 ? void 0 : reference.name) !== null && _f !== void 0 ? _f : undefined,
                                    referenceResolutionFailed: TokenUtils.isReferenceResolutionFailed(token),
                                    referenceOriginRemote: !!((_g = token.origin) === null || _g === void 0 ? void 0 : _g.referenceOriginRemote),
                                    referenceOriginName: (_j = (_h = token.origin) === null || _h === void 0 ? void 0 : _h.referenceOriginName) !== null && _j !== void 0 ? _j : undefined,
                                },
                            ],
                        },
                    ],
                    optionallyShown: [],
                };
            }
            case TokenType.blur: {
                const t = token;
                const reference = TokenUtils.lookupReference((_k = t.value) === null || _k === void 0 ? void 0 : _k.referencedTokenId, allTokens);
                const type = t.value.type === BlurType.background
                    ? "Background blur"
                    : "Layer blur";
                return {
                    alwaysShown: [
                        {
                            key: [this.label("Type")],
                            value: [
                                {
                                    value: type,
                                    prefersReferenceName: false,
                                    referencedName: undefined,
                                },
                            ],
                        },
                        {
                            key: [this.label("Radius")],
                            value: [
                                {
                                    value: DimensionToken.valueToString(t.value.radius),
                                    prefersReferenceName: false,
                                    referencedName: (_l = reference === null || reference === void 0 ? void 0 : reference.name) !== null && _l !== void 0 ? _l : undefined,
                                },
                            ],
                        },
                    ],
                    optionallyShown: [],
                };
            }
            case TokenType.fontFamily:
            case TokenType.fontWeight:
            case TokenType.productCopy:
            case TokenType.string: {
                const t = token;
                const reference = TokenUtils.lookupReference((_m = t.value) === null || _m === void 0 ? void 0 : _m.referencedTokenId, allTokens);
                let label = "";
                switch (t.tokenType) {
                    case TokenType.fontFamily:
                        label = "Font family";
                        break;
                    case TokenType.fontWeight:
                        label = "Font weight";
                        break;
                    case TokenType.string:
                        label = "String";
                        break;
                    case TokenType.productCopy:
                        label = "Product copy";
                        break;
                    default:
                        // @ts-expect-error TS(2339): Property 'tokenType' does not exist on type 'never... Remove this comment to see the full error message
                        throw new Error(`Unexpected token type ${t.tokenType}`);
                }
                return {
                    alwaysShown: [
                        {
                            key: [this.label(label)],
                            value: [
                                {
                                    value: t.value.text,
                                    prefersReferenceName: false,
                                    referencedName: (_o = reference === null || reference === void 0 ? void 0 : reference.name) !== null && _o !== void 0 ? _o : undefined,
                                    referenceResolutionFailed: TokenUtils.isReferenceResolutionFailed(token),
                                    referenceOriginRemote: !!((_p = token.origin) === null || _p === void 0 ? void 0 : _p.referenceOriginRemote),
                                    referenceOriginName: (_r = (_q = token.origin) === null || _q === void 0 ? void 0 : _q.referenceOriginName) !== null && _r !== void 0 ? _r : undefined,
                                },
                            ],
                        },
                    ],
                    optionallyShown: [],
                };
            }
            case TokenType.textCase: {
                const t = token;
                const value = TokenUtils.getTextCaseTokenValue(t.value.value);
                const reference = TokenUtils.lookupReference((_s = t.value) === null || _s === void 0 ? void 0 : _s.referencedTokenId, allTokens);
                return {
                    alwaysShown: [
                        {
                            key: [this.label("Text case")],
                            value: [
                                {
                                    value,
                                    prefersReferenceName: false,
                                    referencedName: (_t = reference === null || reference === void 0 ? void 0 : reference.name) !== null && _t !== void 0 ? _t : undefined,
                                },
                            ],
                        },
                    ],
                    optionallyShown: [],
                };
            }
            case TokenType.textDecoration: {
                const t = token;
                const value = this.textDecorationToString(t.value.value);
                const reference = TokenUtils.lookupReference((_u = t.value) === null || _u === void 0 ? void 0 : _u.referencedTokenId, allTokens);
                return {
                    alwaysShown: [
                        {
                            key: [this.label("Text decoration")],
                            value: [
                                {
                                    value,
                                    prefersReferenceName: false,
                                    referencedName: (_v = reference === null || reference === void 0 ? void 0 : reference.name) !== null && _v !== void 0 ? _v : undefined,
                                },
                            ],
                        },
                    ],
                    optionallyShown: [],
                };
            }
            case TokenType.visibility: {
                const t = token;
                let value = "";
                switch (t.value.value) {
                    case VisibilityType.visible:
                        value = "Visible";
                        break;
                    case VisibilityType.hidden:
                        value = "Hidden";
                        break;
                    default:
                        throw new Error("Unknown visibility option");
                }
                const reference = TokenUtils.lookupReference((_w = t.value) === null || _w === void 0 ? void 0 : _w.referencedTokenId, allTokens);
                return {
                    alwaysShown: [
                        {
                            key: [this.label("Visibility")],
                            value: [
                                {
                                    value,
                                    prefersReferenceName: false,
                                    referencedName: (_x = reference === null || reference === void 0 ? void 0 : reference.name) !== null && _x !== void 0 ? _x : undefined,
                                },
                            ],
                        },
                    ],
                    optionallyShown: [],
                };
            }
            case TokenType.dimension:
            case TokenType.size:
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.duration:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.zIndex:
            case TokenType.radius: {
                const t = token;
                const reference = TokenUtils.lookupReference((_y = t.value) === null || _y === void 0 ? void 0 : _y.referencedTokenId, allTokens);
                let label = "";
                switch (t.tokenType) {
                    case TokenType.dimension:
                        label = "Dimension";
                        break;
                    case TokenType.size:
                        label = "Size";
                        break;
                    case TokenType.space:
                        label = "Space";
                        break;
                    case TokenType.opacity:
                        label = "Opacity";
                        break;
                    case TokenType.duration:
                        label = "Duration";
                        break;
                    case TokenType.fontSize:
                        label = "Font size";
                        break;
                    case TokenType.lineHeight:
                        label = "Line height";
                        break;
                    case TokenType.letterSpacing:
                        label = "Letter spacing";
                        break;
                    case TokenType.paragraphSpacing:
                        label = "Paragraph spacing";
                        break;
                    case TokenType.borderWidth:
                        label = "Border width";
                        break;
                    case TokenType.zIndex:
                        label = "Z-index";
                        break;
                    case TokenType.radius:
                        label = "Radius";
                        break;
                    default:
                        // @ts-expect-error TS(2339): Property 'tokenType' does not exist on type 'never... Remove this comment to see the full error message
                        throw new Error(`Unexpected token type ${t.tokenType}`);
                }
                return {
                    alwaysShown: [
                        {
                            key: [this.label(label)],
                            value: [
                                {
                                    value: DimensionToken.valueToString(t.value),
                                    prefersReferenceName: false,
                                    referencedName: (_z = reference === null || reference === void 0 ? void 0 : reference.name) !== null && _z !== void 0 ? _z : undefined,
                                    referenceResolutionFailed: TokenUtils.isReferenceResolutionFailed(token),
                                    referenceOriginRemote: !!((_0 = token.origin) === null || _0 === void 0 ? void 0 : _0.referenceOriginRemote),
                                    referenceOriginName: (_2 = (_1 = token.origin) === null || _1 === void 0 ? void 0 : _1.referenceOriginName) !== null && _2 !== void 0 ? _2 : undefined,
                                },
                            ],
                        },
                    ],
                    optionallyShown: [],
                };
            }
            case TokenType.border: {
                const t = token;
                let style = "";
                let position = "";
                switch (t.value.style) {
                    case BorderStyle.dashed:
                        style = "Dashed";
                        break;
                    case BorderStyle.dotted:
                        style = "Dotted";
                        break;
                    case BorderStyle.solid:
                        style = "Solid";
                        break;
                    case BorderStyle.groove:
                        style = "Groove";
                        break;
                    default:
                        break;
                }
                switch (t.value.position) {
                    case BorderPosition.center:
                        position = "Center";
                        break;
                    case BorderPosition.inside:
                        position = "Inside";
                        break;
                    case BorderPosition.outside:
                        position = "Outside";
                        break;
                    default:
                        break;
                }
                return {
                    alwaysShown: [
                        {
                            key: [this.label("Border width")],
                            value: [
                                {
                                    value: DimensionToken.valueToString(t.value.width),
                                    prefersReferenceName: false,
                                    referencedName: (_6 = (_5 = TokenUtils.lookupReference((_4 = (_3 = t.value) === null || _3 === void 0 ? void 0 : _3.width) === null || _4 === void 0 ? void 0 : _4.referencedTokenId, allTokens)) === null || _5 === void 0 ? void 0 : _5.name) !== null && _6 !== void 0 ? _6 : undefined,
                                },
                            ],
                        },
                        {
                            key: [this.label("Color")],
                            value: [
                                {
                                    value: ColorToken.colorValueToHex8(t.value.color).toUpperCase(),
                                    prefersReferenceName: true,
                                    referencedName: (_10 = (_9 = TokenUtils.lookupReference((_8 = (_7 = t.value) === null || _7 === void 0 ? void 0 : _7.color) === null || _8 === void 0 ? void 0 : _8.referencedTokenId, allTokens)) === null || _9 === void 0 ? void 0 : _9.name) !== null && _10 !== void 0 ? _10 : undefined,
                                },
                            ],
                        },
                        {
                            key: [this.label("Style")],
                            value: [
                                {
                                    value: style,
                                    prefersReferenceName: false,
                                    referencedName: undefined,
                                },
                            ],
                        },
                        {
                            key: [this.label("Position")],
                            value: [
                                {
                                    value: position,
                                    prefersReferenceName: false,
                                    referencedName: undefined,
                                },
                            ],
                        },
                    ],
                    optionallyShown: [],
                };
            }
            case TokenType.typography: {
                const t = token;
                return {
                    alwaysShown: [
                        {
                            key: [this.label("Font family")],
                            value: [
                                {
                                    value: t.value.fontFamily.text,
                                    prefersReferenceName: false,
                                    ...TokenUtilPreviewSegmentGenerator.getTypographyValueReferenceProperties(t.value.fontFamily, allTokens),
                                },
                            ],
                        },
                        {
                            key: [this.label("Font weight/style")],
                            value: [
                                {
                                    value: t.value.fontWeight.text,
                                    prefersReferenceName: false,
                                    ...TokenUtilPreviewSegmentGenerator.getTypographyValueReferenceProperties(t.value.fontWeight, allTokens),
                                },
                            ],
                        },
                        {
                            key: [this.label("Font size")],
                            value: [
                                {
                                    value: DimensionToken.valueToString(t.value.fontSize),
                                    prefersReferenceName: false,
                                    ...TokenUtilPreviewSegmentGenerator.getTypographyValueReferenceProperties(t.value.fontSize, allTokens),
                                },
                            ],
                        },
                        {
                            key: [this.label("Line height")],
                            value: [
                                {
                                    value: t.value.lineHeight
                                        ? DimensionToken.valueToString(t.value.lineHeight)
                                        : "Default",
                                    prefersReferenceName: false,
                                    ...TokenUtilPreviewSegmentGenerator.getTypographyValueReferenceProperties(t.value.lineHeight, allTokens),
                                },
                            ],
                        },
                    ],
                    optionallyShown: [
                        {
                            key: [this.label("Text case")],
                            value: [
                                {
                                    value: t.value.textCase
                                        ? TokenUtils.getTextCaseTokenValue(t.value.textCase.value)
                                        : "Original",
                                    prefersReferenceName: false,
                                    ...TokenUtilPreviewSegmentGenerator.getTypographyValueReferenceProperties(t.value.textCase, allTokens),
                                },
                            ],
                        },
                        {
                            key: [this.label("Text decoration")],
                            value: [
                                {
                                    value: t.value.textDecoration
                                        ? this.textDecorationToString(t.value.textDecoration.value)
                                        : "None",
                                    prefersReferenceName: false,
                                    ...TokenUtilPreviewSegmentGenerator.getTypographyValueReferenceProperties(t.value.textDecoration, allTokens),
                                },
                            ],
                        },
                        {
                            key: [this.label("Letter spacing")],
                            value: [
                                {
                                    value: t.value.letterSpacing
                                        ? DimensionToken.valueToString(t.value.letterSpacing)
                                        : "Default",
                                    prefersReferenceName: false,
                                    ...TokenUtilPreviewSegmentGenerator.getTypographyValueReferenceProperties(t.value.letterSpacing, allTokens),
                                },
                            ],
                        },
                        {
                            key: [this.label("Paragraph spacing")],
                            value: [
                                {
                                    value: t.value.paragraphSpacing
                                        ? DimensionToken.valueToString(t.value.paragraphSpacing)
                                        : "Default",
                                    prefersReferenceName: false,
                                    ...TokenUtilPreviewSegmentGenerator.getTypographyValueReferenceProperties(t.value.paragraphSpacing, allTokens),
                                },
                            ],
                        },
                        {
                            key: [this.label("Paragraph indent")],
                            value: [
                                {
                                    value: t.value.paragraphIndent
                                        ? DimensionToken.valueToString(t.value.paragraphIndent)
                                        : "Default",
                                    prefersReferenceName: false,
                                    ...TokenUtilPreviewSegmentGenerator.getTypographyValueReferenceProperties(t.value.paragraphIndent, allTokens),
                                },
                            ],
                        },
                    ],
                };
            }
            default:
                return {
                    alwaysShown: [],
                    optionallyShown: [],
                };
        }
    }
    resolvedInfoForMultiLayerToken(token, layerIndex, allTokens) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        switch (token.tokenType) {
            case TokenType.shadow: {
                const layer = sureOf(token.value[layerIndex]);
                let shadowType = "";
                switch (layer.type) {
                    case ShadowType.drop:
                        shadowType = "Drop shadow";
                        break;
                    case ShadowType.inner:
                        shadowType = "Inner shadow";
                        break;
                    default:
                        throw new Error(`Unexpected shadow type ${String(layer.type)}`);
                }
                const referencedColorToken = TokenUtils.lookupReference(layer.color.referencedTokenId, allTokens);
                return {
                    alwaysShown: [
                        {
                            key: [this.label("Type")],
                            value: [
                                {
                                    value: shadowType,
                                    prefersReferenceName: true,
                                    referencedName: undefined,
                                },
                            ],
                        },
                        {
                            key: [this.label("X, Y")],
                            value: [
                                {
                                    value: `${Math.round(layer.x)}px, ${Math.round(layer.y)}px`,
                                    prefersReferenceName: false,
                                    referencedName: undefined,
                                },
                            ],
                        },
                        {
                            key: [this.label("Blur")],
                            value: [
                                {
                                    value: `${Math.round(layer.radius)}px`,
                                    prefersReferenceName: false,
                                    referencedName: undefined,
                                },
                            ],
                        },
                        {
                            key: [this.label("Spread")],
                            value: [
                                {
                                    value: `${Math.round(layer.spread)}px`,
                                    prefersReferenceName: false,
                                    referencedName: undefined,
                                },
                            ],
                        },
                        {
                            key: [this.label("Color")],
                            forceRawValue: false,
                            value: [
                                {
                                    value: ColorToken.colorValueToHex6(layer.color).toUpperCase(),
                                    prefersReferenceName: true,
                                    referencedName: referencedColorToken &&
                                        TokenUtilPreviewSegmentGenerator.getReferenceName(referencedColorToken),
                                    referenceOriginRemote: !!((_a = referencedColorToken === null || referencedColorToken === void 0 ? void 0 : referencedColorToken.origin) === null || _a === void 0 ? void 0 : _a.referenceOriginRemote),
                                    referenceOriginName: (_c = (_b = referencedColorToken === null || referencedColorToken === void 0 ? void 0 : referencedColorToken.origin) === null || _b === void 0 ? void 0 : _b.referenceOriginName) !== null && _c !== void 0 ? _c : undefined,
                                    referenceResolutionFailed: TokenUtils.isReferenceResolutionFailed(token),
                                },
                            ],
                        },
                        {
                            key: [this.label("Opacity")],
                            value: [
                                {
                                    value: `${Math.round(((_e = (_d = layer === null || layer === void 0 ? void 0 : layer.opacity) === null || _d === void 0 ? void 0 : _d.measure) !== null && _e !== void 0 ? _e : layer.color.opacity.measure) *
                                        100)}%`,
                                    prefersReferenceName: false,
                                    referencedName: (_h = (_g = TokenUtils.lookupReference((_f = layer === null || layer === void 0 ? void 0 : layer.opacity) === null || _f === void 0 ? void 0 : _f.referencedTokenId, allTokens)) === null || _g === void 0 ? void 0 : _g.name) !== null && _h !== void 0 ? _h : undefined,
                                },
                            ],
                        },
                    ],
                    optionallyShown: [],
                };
            }
            case TokenType.gradient: {
                const value = sureOf(token.value[layerIndex]);
                const transform = new TokenTransform();
                const stops = value.stops.flatMap((stop, index) => {
                    var _a, _b, _c;
                    const colorReference = TokenUtils.lookupReference(stop.color.referencedTokenId, allTokens);
                    return [
                        {
                            value: transform.colorTokenToCSS(stop.color),
                            prefersReferenceName: true,
                            referencedName: colorReference &&
                                TokenUtilPreviewSegmentGenerator.getReferenceName(colorReference),
                            referenceOriginRemote: !!((_a = colorReference === null || colorReference === void 0 ? void 0 : colorReference.origin) === null || _a === void 0 ? void 0 : _a.referenceOriginRemote),
                            referenceOriginName: (_c = (_b = colorReference === null || colorReference === void 0 ? void 0 : colorReference.origin) === null || _b === void 0 ? void 0 : _b.referenceOriginName) !== null && _c !== void 0 ? _c : undefined,
                            referenceResolutionFailed: TokenUtils.isReferenceResolutionFailed(token),
                        },
                        {
                            // add comma if not last stop here instead of separate segment to prevent having comma on separate line or having gap
                            // if this will be anytime referenced, then it can be moved to separate segment
                            value: `${stop.position * 100}%${index < value.stops.length - 1 ? "," : ")"}`,
                        },
                    ];
                });
                const gradientTokenCssDirection = transform.gradientTokenToCssDirection(value);
                let gradientTypeCssString = `${transform.gradientTokenToCssFunction(value)}(`;
                if (gradientTokenCssDirection) {
                    gradientTypeCssString += `${gradientTokenCssDirection},`;
                }
                return {
                    alwaysShown: [
                        {
                            key: [
                                {
                                    value: gradientTypeCssString,
                                },
                                ...stops,
                            ],
                            fullWidth: true,
                            wrapSegments: true,
                        },
                    ],
                    optionallyShown: [],
                };
            }
            default:
                return {
                    alwaysShown: [],
                    optionallyShown: [],
                };
        }
    }
    label(text) {
        return {
            value: text,
            prefersReferenceName: false,
            referencedName: undefined,
        };
    }
    textDecorationToString(textDecoration) {
        switch (textDecoration) {
            case TextDecoration.original:
                return "None";
            case TextDecoration.strikethrough:
                return "Strikethrough";
            case TextDecoration.underline:
                return "Underline";
            default:
                throw new Error("Unknown text decoration");
        }
    }
}
